///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faLock, faCreditCard } from '@fortawesome/free-solid-svg-icons'

import { useAuth } from '../../context/AuthContext'
import { createNotification } from '../../services/NotificationService'
import { formatNr } from '../../services/FormatService'

//-----------------------------------------------------------------------------------------
//* \brief
//? Billing details the customer confirms right before being sent to NETOPIA.
//?
//? NETOPIA requires a full billing address including a postal code, which the platform
//? never asked for until now, so the form is pre-filled from the profile and asks only for
//? what is missing. Whatever is confirmed here is stored back on the account, so returning
//? customers only ever see it filled in.
//?
//? @param invoice      the unpaid invoice being paid
//? @param onClose      called when the customer backs out
//? @param justCreated  true when the order was created moments ago by choosing "pay by
//?                     card", so backing out has to say where the order went
//-----------------------------------------------------------------------------------------
const CardPaymentForm = ({ invoice, onClose, justCreated = false }) => {

    const { userData, currentUser, startCardPayment } = useAuth()

    const profile = (userData && userData.data) || {}

    const [sending, setSending] = useState(false)
    const [form, setForm] = useState({
        phone: profile.phone || '',
        city: profile.city || '',
        county: profile.county || (invoice.client && invoice.client.county) || '',
        address: profile.address || (invoice.client && invoice.client.address) || '',
        postalCode: profile.postalCode || ''
    })

    const setField = (field) => (event) =>
        setForm(prev => ({ ...prev, [field]: event.target.value }))

    //? Same arithmetic as the invoice itself, so the figure shown here is the figure that
    //? the backend recomputes and charges.
    const totalToPay = () => {
        const rate = +invoice.currencyRate
        const net = (+invoice.subscription.priceEur * rate).toFixed(2) * (+invoice.subscription.quantity)
        const tva = (0.21 * net).toFixed(2)
        return (+tva + net).toFixed(2)
    }

    const validate = () => {
        if (form.phone.trim().length < 10) return 'Introduceți un număr de telefon valid (minim 10 cifre).'
        if (form.city.trim() === '') return 'Completați localitatea.'
        if (form.county.trim() === '') return 'Completați județul.'
        if (form.address.trim() === '') return 'Completați adresa.'
        // Romanian postal codes are exactly six digits
        if (!/^\d{6}$/.test(form.postalCode.trim())) return 'Codul poștal trebuie să conțină 6 cifre.'
        return ''
    }

    const handlePay = async () => {
        if (sending) return

        const problem = validate()
        if (problem !== '') {
            createNotification('warning', problem, 'Date incomplete')
            return
        }

        setSending(true)
        const result = await startCardPayment(invoice.id, {
            phone: form.phone.trim(),
            city: form.city.trim(),
            county: form.county.trim(),
            address: form.address.trim(),
            postalCode: form.postalCode.trim()
        })

        //? On success the browser is already navigating to NETOPIA, so the button is left
        //? spinning on purpose - re-enabling it would invite a second order.
        if (!result.ok) setSending(false)
    }

    return (
        <div className='md-form'>

            <div className='md-summary'>
                <div className='md-summary__item'>
                    <span>Factură</span>
                    <span>{invoice.seriesNumber}</span>
                </div>
                <div className='md-summary__item'>
                    <span>Abonament</span>
                    <span>{invoice.subscription.name} - {invoice.subscription.period} luni</span>
                </div>
                <div className='md-summary__item'>
                    <span>Curs BNR folosit</span>
                    <span>1 EUR = {invoice.currencyRate} Lei</span>
                </div>
                <div className='md-summary__item'>
                    <span>Total de plată (TVA inclus)</span>
                    <span style={{color:'var(--contrast-font-color)'}}>
                        <b>{formatNr(totalToPay())} Lei</b>
                    </span>
                </div>
            </div>

            <div className='md-form__divider'></div>

            <div className='ap-popup' style={{textAlign:'left'}}>
                <div className='ap-popup-info'>
                    <div>
                        Verificați datele de facturare. Acestea sunt necesare procesatorului de plăți
                        și vor fi salvate în contul dumneavoastră pentru plățile viitoare.
                    </div>
                </div>

                <div className='ap-popup-field'>
                    <label className='ap-popup-label' htmlFor='cpFirma'>Firmă</label>
                    <input className='input-text' type='text' id='cpFirma'
                        disabled={true} readOnly={true}
                        defaultValue={(invoice.client && invoice.client.name) || profile.company || ''} />
                    <span className='ap-popup-hint'>Se modifică din secțiunea "Profil".</span>
                </div>

                <div className='ap-popup-field'>
                    <label className='ap-popup-label' htmlFor='cpCui'>CUI</label>
                    <input className='input-text' type='text' id='cpCui'
                        disabled={true} readOnly={true}
                        defaultValue={(invoice.client && invoice.client.cui) || profile.cui || ''} />
                </div>

                <div className='ap-popup-field'>
                    <label className='ap-popup-label' htmlFor='cpEmail'>Email</label>
                    <input className='input-text' type='text' id='cpEmail'
                        disabled={true} readOnly={true}
                        defaultValue={(invoice.client && invoice.client.email) || (currentUser && currentUser.email) || ''} />
                </div>

                <div className='ap-popup-field'>
                    <label className='ap-popup-label' htmlFor='cpPhone'>Telefon</label>
                    <input className='input-text' type='tel' id='cpPhone'
                        value={form.phone} onChange={setField('phone')} required />
                </div>

                <div className='ap-popup-field'>
                    <label className='ap-popup-label' htmlFor='cpCity'>Localitate</label>
                    <input className='input-text' type='text' id='cpCity'
                        value={form.city} onChange={setField('city')} required />
                </div>

                <div className='ap-popup-field'>
                    <label className='ap-popup-label' htmlFor='cpCounty'>Județ</label>
                    <input className='input-text' type='text' id='cpCounty'
                        value={form.county} onChange={setField('county')} required />
                </div>

                <div className='ap-popup-field'>
                    <label className='ap-popup-label' htmlFor='cpAddress'>Adresă</label>
                    <input className='input-text' type='text' id='cpAddress'
                        value={form.address} onChange={setField('address')} required />
                </div>

                <div className='ap-popup-field'>
                    <label className='ap-popup-label' htmlFor='cpZip'>Cod poștal</label>
                    <input className='input-text' type='text' id='cpZip' maxLength={6}
                        placeholder='ex. 400001'
                        value={form.postalCode} onChange={setField('postalCode')} required />
                    <span className='ap-popup-hint'>Obligatoriu pentru plata cu cardul (6 cifre).</span>
                </div>

                <div className='ap-popup-divider'></div>

                <div className='ap-popup-info'>
                    <div><b>Ce urmează:</b></div>
                    <div>1. Veți fi redirecționat către pagina securizată <b>NETOPIA Payments</b>.</div>
                    <div>2. Introduceți acolo datele cardului. <b>Acestea nu trec și nu sunt stocate pe serverele noastre.</b></div>
                    <div>3. După confirmarea plății reveniți automat în aplicație, iar <b>abonamentul se activează singur</b>, în câteva secunde.</div>
                    <div>4. Factura fiscală devine disponibilă imediat în secțiunea "Plăți" și o primiți și pe email.</div>
                </div>

                <div className='ap-popup-hint' style={{marginTop:'0.5em'}}>
                    Dacă plata nu reușește, nu se reține nicio sumă, iar comanda rămâne valabilă -
                    puteți reîncerca sau puteți plăti prin transfer bancar.
                </div>

                {justCreated &&
                    <div className='ap-popup-hint' style={{marginTop:'0.5em'}}>
                        Comanda <b>{invoice.seriesNumber}</b> a fost deja înregistrată. Dacă renunțați acum,
                        o găsiți în secțiunea <b>"Plăți"</b>, de unde o puteți achita mai târziu (card sau
                        transfer bancar) sau o puteți anula.
                    </div>
                }
            </div>

            <div className='md-footer' style={{marginTop:'1em'}}>
                <button className='md-btn md-btn--ghost' onClick={onClose} disabled={sending}>
                    Renunță
                </button>
                <button className='md-btn md-btn--primary' onClick={handlePay}>
                    {sending
                        ? <><FontAwesomeIcon className='spinner' icon={faSpinner}/> Se deschide pagina de plată...</>
                        : <><FontAwesomeIcon icon={faLock}/> Continuă către plata securizată</>
                    }
                </button>
            </div>

            <div style={{textAlign:'center', marginTop:'0.75em', opacity:0.7, fontSize:'0.85em'}}>
                <FontAwesomeIcon icon={faCreditCard}/> Plată procesată de NETOPIA Payments - Visa / Mastercard
            </div>
        </div>
    )
}

export default CardPaymentForm
